import React from 'react';
import { ComingSoonPage } from '@components/ui/pages';
import { Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useTracking } from '../../../context/TrackingContext';
import { EventType } from '../../../types/events';

/**
 * EligibilityChecksPage Component
 *
 * A "coming soon" page for the Eligibility Checks feature.
 * Displays information about the upcoming eligibility checks feature
 * with CTA for learning more.
 */
export const EligibilityChecksPage: React.FC = () => {
  const { eligibilityChecks } = useEventTracking();
  const { trackEvent } = useTracking();

  const handleLearnMoreClick = () => {
    eligibilityChecks.learnMoreClicked({
      destination_url: 'https://calendly.com/npeereboom/jotcoach-introduction-clone',
    });
    trackEvent(EventType.EXPRESSED_INTEREST, {
      feature: 'eligibility-checks',
    });
  };

  return (
    <ComingSoonPage
      sx={{ justifyContent: 'center' }}
      title="Eligibility Checks"
      showHeader={false}
      fullHeight
      heroTitle="Eligibility Checks"
      description={
        <>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>
            Understanding a patient&apos;s true benefits requires more than a yes/no
          </Typography>
          <Typography component="p" sx={{ mb: 2 }}>
            Unlike traditional eligibility checks that stop at coverage status, we go deeper into{' '}
            <strong>how the plan actually behaves at the CPT claim level</strong>.
          </Typography>
          <Typography component="p" sx={{ mb: 2 }}>
            We interpret service-level coverage for the specific visit type and CPT codes being
            billed, surface behavioral-health carve-outs and exclusions, and identify prior
            authorization requirements, referral rules, and visit limits that materially affect
            reimbursement. The result is{' '}
            <strong>
              eligibility intelligence that reflects how the claim will adjudicate—not just whether
              the patient appears &quot;active.&quot;
            </strong>
          </Typography>
          <Typography component="p" sx={{ mb: 2 }}>
            Before each appointment, we verify that coverage is active and usable, using{' '}
            <strong>
              payer APIs, AI-assisted plan document identification, and a live insurer call when
              necessary
            </strong>
            .
          </Typography>
          <Typography component="p">
            <strong>Just 90 cents per check.</strong>
          </Typography>
        </>
      }
      primaryAction={{
        label: 'Learn more',
        href: 'https://calendly.com/npeereboom/jotcoach-introduction-clone',
        external: true,
        onClick: handleLearnMoreClick,
        startIcon: null,
      }}
    />
  );
};
